import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  Button,
  Input,
  Label,
  Table,
  TableHeader,
  TableCell,
  TableBody,
  TableRow,
} from "@windmill/react-ui";
import InputAreaDate from "components/form/InputAreaDate";
import salesServices from "services/SalesService";
import { notifySuccess, notifyError } from "utils/toast";

const ReceivedAmtModal = ({
  isOpen,
  data,
  onClose,
  mode = "add",
  onSuccess,
  canEdit,
}) => {
  // console.log(data)
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);
  const [adjustData, setAdjustData] = useState(null);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [selectedAdjustments, setSelectedAdjustments] = useState([]);
  const [deductedAmounts, setDeductedAmounts] = useState({});
  const [hasManuallyToggled, setHasManuallyToggled] = useState(false);

  const payableAmount =
    mode === "edit"
      ? Number(data?.receivable_amt || 0)
      : Number(data?.payable_amt || 0);

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && data) {
        const receivedAmt = Number(data.received_amt);
        setAmount(isNaN(receivedAmt) ? "" : receivedAmt.toFixed(2));
        setDate(data.received_date || "");
        setPaymentType(data.payment_type || "");
        setRemark(data.remark || "");
        if (data.payment_type === "Adjust") {
          setSelectedAdjustments([]);
          setDeductedAmounts({});
          setAdjustData(null);
        } else {
          setSelectedAdjustments(data.adjustments || []);
          setDeductedAmounts({});
        }
        setHasManuallyToggled(false);
      } else {
        resetFields();
      }
    } else {
      resetFields();
    }
    // console.log("----",data)
  }, [isOpen, mode, data]);

  useEffect(() => {
    if (
      isOpen &&
      paymentType === "Adjust" &&
      data?.agent_id &&
      !(mode === "edit" && data?.payment_type === "Adjust")
    ) {
      // console.log("Fetching adjustments for agent_id:", data.agent_id);
      fetchAdjustData(data.agent_id);
    } else {
      setAdjustData(null);
      setAdjustLoading(false);
      setSelectedAdjustments([]);
      setDeductedAmounts({});
      // Do not clear amount to preserve user-entered or data.received_amt
    }
  }, [paymentType, isOpen, data?.agent_id, mode, data?.payment_type]);

  useEffect(() => {
    if (
      paymentType === "Adjust" &&
      adjustData?.length > 0 &&
      payableAmount > 0 &&
      !hasManuallyToggled &&
      !(mode === "edit" && data?.payment_type === "Adjust")
    ) {
      // console.log("Running auto-selection", { adjustData, payableAmount });
      const sortedAdjustments = [...adjustData].sort(
        (a, b) =>
          Number(b.refundable_amount || 0) - Number(a.refundable_amount || 0),
      );
      let remainingAmount = payableAmount;
      const autoSelected = [];
      const tempDeductedAmounts = {};

      for (const adjustment of sortedAdjustments) {
        if (remainingAmount >= Number(adjustment.refundable_amount || 0)) {
          autoSelected.push(adjustment.id);
          tempDeductedAmounts[adjustment.id] = Number(
            adjustment.refundable_amount || 0,
          );
          remainingAmount -= Number(adjustment.refundable_amount || 0);
        } else if (remainingAmount > 0) {
          autoSelected.push(adjustment.id);
          tempDeductedAmounts[adjustment.id] = remainingAmount;
          remainingAmount = 0;
        }
      }

      setSelectedAdjustments(autoSelected);
      setDeductedAmounts(tempDeductedAmounts);
      const totalSelectedAmount = Object.values(tempDeductedAmounts).reduce(
        (sum, val) => sum + Number(val),
        0,
      );
      setAmount(totalSelectedAmount > 0 ? totalSelectedAmount.toFixed(2) : "");
      // console.log("Auto-selected adjustments:", autoSelected, "Total amount:", totalSelectedAmount, "Deducted amounts:", tempDeductedAmounts);
    }
  }, [
    adjustData,
    paymentType,
    payableAmount,
    hasManuallyToggled,
    mode,
    data?.payment_type,
  ]);

  const resetFields = () => {
    setAmount("");
    setDate("");
    setPaymentType("");
    setRemark("");
    setAdjustData(null);
    setAdjustLoading(false);
    setSelectedAdjustments([]);
    setDeductedAmounts({});
    setHasManuallyToggled(false);
  };

  const fetchAdjustData = async (agentId) => {
    try {
      setAdjustLoading(true);
      const res = await salesServices.getRefundById(agentId);
      // console.log("API Response:", res);
      if (res?.status && res.data) {
        const validAdjustments = Array.isArray(res.data)
          ? res.data.map((item) => ({
              ...item,
              id: String(
                item.client_id ||
                  item.id ||
                  `temp-${Math.random().toString(36).substr(2, 9)}`,
              ),
              refundable_amount: Number(item.refundable_amount || 0),
            }))
          : {
              ...res.data,
              id: String(
                res.data.client_id ||
                  res.data.id ||
                  `temp-${Math.random().toString(36).substr(2, 9)}`,
              ),
              refundable_amount: Number(res.data.refundable_amount || 0),
            };
        const uniqueAdjustments = [];
        const seenIds = new Set();
        const adjustmentsArray = Array.isArray(validAdjustments)
          ? validAdjustments
          : [validAdjustments];
        for (const adj of adjustmentsArray) {
          if (!seenIds.has(adj.id)) {
            uniqueAdjustments.push(adj);
            seenIds.add(adj.id);
          } else {
            console.warn(`Duplicate adjustment ID found: ${adj.id}`);
          }
        }
        // console.log("Processed adjustments:", uniqueAdjustments);
        setAdjustData(uniqueAdjustments.length > 0 ? uniqueAdjustments : null);
      } else {
        console.log("No valid data in API response");
        setAdjustData(null);
      }
    } catch (error) {
      console.error("Error fetching adjustments:", error);
      setAdjustData(null);
      notifyError("Failed to fetch adjustments.");
    } finally {
      setAdjustLoading(false);
    }
  };

  const handleAdjustmentToggle = (adjustmentId) => {
    setHasManuallyToggled(true);
    const adjustment = adjustData.find(
      (item) => item.id === String(adjustmentId),
    );
    if (!adjustment) {
      console.warn(`Adjustment with ID ${adjustmentId} not found`);
      return;
    }

    console.log(
      "Toggling adjustment:",
      adjustmentId,
      "Current selected:",
      selectedAdjustments,
    );
    let newSelected = [...selectedAdjustments];
    let newDeductedAmounts = { ...deductedAmounts };

    if (selectedAdjustments.includes(String(adjustmentId))) {
      newSelected = selectedAdjustments.filter(
        (id) => id !== String(adjustmentId),
      );
      delete newDeductedAmounts[adjustmentId];
      setSelectedAdjustments(newSelected);
      setDeductedAmounts(newDeductedAmounts);
      const newTotal = Object.values(newDeductedAmounts).reduce(
        (sum, val) => sum + Number(val),
        0,
      );
      setAmount(newTotal > 0 ? newTotal.toFixed(2) : "");
      console.log(
        "Deselected adjustment:",
        adjustmentId,
        "New total:",
        newTotal,
        "New deducted amounts:",
        newDeductedAmounts,
      );
    } else {
      const currentTotal = Object.values(newDeductedAmounts).reduce(
        (sum, val) => sum + Number(val),
        0,
      );
      const remainingAmount = payableAmount - currentTotal;
      const adjustmentAmount = Number(adjustment.refundable_amount || 0);

      if (remainingAmount <= 0) {
        notifyError("No remaining payable amount to cover this adjustment.");
        return;
      }

      if (adjustmentAmount <= 0) {
        notifyError(
          "This booking has no remaining amount available to adjust.",
        );
        return;
      }

      newSelected = [...selectedAdjustments, String(adjustmentId)];
      if (adjustmentAmount <= remainingAmount) {
        newDeductedAmounts[adjustmentId] = adjustmentAmount;
      } else {
        newDeductedAmounts[adjustmentId] = remainingAmount;
      }

      setSelectedAdjustments(newSelected);
      setDeductedAmounts(newDeductedAmounts);
      const newTotal = Object.values(newDeductedAmounts).reduce(
        (sum, val) => sum + Number(val),
        0,
      );
      setAmount(newTotal.toFixed(2));
      // console.log("Selected adjustment:", adjustmentId, "New total:", newTotal, "New deducted amounts:", newDeductedAmounts);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    const adjustmentsPayload = selectedAdjustments.map((id) => {
      const adjustment = adjustData.find((item) => item.id === id);
      return {
        client_id: id,
        used_amount: Number(deductedAmounts[id] || 0),
        total_amount: Number(adjustment?.refundable_amount || 0),
      };
    });

    // GST/TDS/receivable/balance are computed server-side (see computeGstTds
    // in salesModel.js) — only raw payment inputs are sent here. The server
    // never trusts client-provided money figures.
    const payload = {
      client_id: data.client_id,
      agent_id: data.agent_id,
      booking_id: data.booking_id || data.client_ref,
      received_amt:
        mode === "edit" && data?.payment_type === "Adjust"
          ? Number(data.received_amt) || 0
          : parseFloat(amount || "0"),
      received_date: (() => {
        if (!date) return "";
        if (typeof date === "string") return date; // already normalized
        const local = new Date(
          date.getTime() - date.getTimezoneOffset() * 60000,
        )
          .toISOString()
          .split("T")[0];
        return local;
      })(),
      payment_type: paymentType,
      remark,
      adjustments: paymentType === "Adjust" ? adjustmentsPayload : [],
    };

    try {
      const res =
        mode === "edit" && data?.id
          ? await salesServices.updateSales(data.id, payload)
          : await salesServices.addPayment(payload);

      if (res.status) {
        notifySuccess(res.message || "Payment saved.");
        onSuccess?.({ success: true });
        onClose();
      } else {
        notifyError(res.message || "Failed to save.");
      }
    } catch (err) {
      notifyError(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      className="w-full max-w-3xl bg-white"
    >
      <ModalBody
        className="p-8 bg-white"
        style={{ height: "600px", overflowY: "scroll", scrollbarWidth: "thin" }}
      >
        <h2 className="text-lg font-semibold mb-4">
          {mode === "edit"
            ? "Edit Received Amount"
            : `Add Received Amount for ${data?.client_ref}`}
        </h2>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label>
              <span>GST Amount</span>:{" "}
              {(Number(data?.gst_amount) || 0).toFixed(2)}
            </Label>
            <Label>
              <span>TDS Amount</span>:{" "}
              {(Number(data?.tds_amount) || 0).toFixed(2)}
            </Label>

            {/* <Label>
              <span>
                {(() => {
                  // If trip is cancelled, directly show receivable_amt
                  if (data.trip_status === 1) {
                    return `Receivable Amount: ${parseFloat(data.receivable_amt || 0)}`;
                  }

                  let previousBalance = parseFloat(data.receivable_amt || 0);

                  // If in edit mode, calculate up to the current record
                  if (mode === "edit" && Array.isArray(data.sales_records)) {
                    const recordIndex = data.sales_records.findIndex(
                      (r) => r.id === data.recordId,
                    );
                    for (let i = 0; i <= recordIndex; i++) {
                      const receivedAmt = parseFloat(
                        data.sales_records[i]?.received_amt || 0,
                      );
                      previousBalance -= receivedAmt;
                    }
                  }

                  // If in add mode, subtract all existing received amounts
                  if (mode === "add" && Array.isArray(data.sales_records)) {
                    for (const rec of data.sales_records) {
                      const receivedAmt = parseFloat(rec.received_amt || 0);
                      previousBalance -= receivedAmt;
                    }
                  }

                  const labelType =
                    previousBalance >= 0 ? "Receivable" : "Payable";
                  return `${labelType} Amount: ${Math.abs(previousBalance)}`;
                })()}
              </span>
            </Label> */}

            <Label>
              <span>Payable Amount:{parseFloat(data.payable_amt || 0)}</span>
            </Label>
          </div>

          <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
            <Label className="flex-1">
              <span>Amount</span>
              <Input
                type="text"
                className="pt-2 pb-2 border border-dark"
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    setAmount(value);
                  }
                }}
                disabled={canEdit || saving || paymentType === "Adjust"}
              />
              {mode === "edit" && paymentType === "Adjust" && (
                <p className="text-sm text-gray-500 mt-1">
                  {/* Optional message or note can go here */}
                </p>
              )}
            </Label>

            <Label className="flex-1">
              <span>Date</span>
              <InputAreaDate
                value={date}
                onChange={setDate}
                disabled={saving}
              />
            </Label>

            <Label className="flex-1">
              <span>Payment Type</span>
              <select
                value={paymentType}
                onChange={(e) => {
                  setPaymentType(e.target.value);
                  setHasManuallyToggled(false);
                }}
                disabled={
                  saving || (mode === "edit" && data?.payment_type === "Adjust")
                }
                className="block w-full mt-1 border border-gray-300 rounded py-2 px-3"
              >
                <option value="">Select</option>
                <option value="Online">Online</option>
                <option value="Cash">Cash</option>
                <option value="Adjust" disabled={canEdit}>
                  Adjust
                </option>
              </select>

              {mode === "edit" && data?.payment_type === "Adjust" && (
                <p className="text-sm text-gray-500 mt-1">
                  Payment type cannot be changed from Adjust in edit mode.
                </p>
              )}
            </Label>
          </div>

          <Label>
            <span>Remark</span>
            <Input
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              disabled={saving}
              className="border border-dark"
            />
          </Label>

          {paymentType === "Adjust" &&
            !(mode === "edit" && data?.payment_type === "Adjust") && (
              <>
                {adjustData?.filter(
                  (item) => Number(item.refundable_amount) > 0,
                ).length > 0 && (
                  <div className="mt-2">
                    <h3 className="text-md font-semibold mb-2">Adjustments</h3>
                    <Table>
                      <TableHeader>
                        <tr>
                          <TableCell>Select</TableCell>
                          <TableCell>Booking ID</TableCell>
                          <TableCell>Refundable Amount</TableCell>
                          <TableCell>Used Amount</TableCell>
                          <TableCell>Reason</TableCell>
                        </tr>
                      </TableHeader>
                      <TableBody>
                        {adjustData
                          .filter((item) => Number(item.refundable_amount) > 0)
                          .map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <input
                                  type="checkbox"
                                  checked={selectedAdjustments.includes(
                                    item.id,
                                  )}
                                  onChange={() =>
                                    handleAdjustmentToggle(item.id)
                                  }
                                  disabled={saving}
                                />
                              </TableCell>
                              <TableCell>{item.client_ref || "N/A"}</TableCell>
                              <TableCell>
                                {parseFloat(
                                  item.refundable_amount || 0,
                                ).toFixed(2)}
                              </TableCell>
                              <TableCell>
                                {selectedAdjustments.includes(item.id)
                                  ? parseFloat(
                                      deductedAmounts[item.id] || 0,
                                    ).toFixed(2)
                                  : "0.00"}
                              </TableCell>
                              <TableCell>{item.reason || "N/A"}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            )}

          {paymentType === "Adjust" &&
            adjustLoading &&
            !(mode === "edit" && data?.payment_type === "Adjust") && (
              <div className="mt-2">Loading adjustments...</div>
            )}

          {paymentType === "Adjust" &&
            !adjustLoading &&
            (!adjustData || adjustData.length === 0) &&
            !(mode === "edit" && data?.payment_type === "Adjust") && (
              // <div className="mt-2 text-red-600">No adjustments available.</div>
              <div className="mt-2 text-red-600">    No adjustments available. Please choose another payment type.</div>
            )}

          <div className="flex justify-end pt-4 space-x-3">
            <Button layout="outline" onClick={() => !saving && onClose()}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? "Saving..." : mode === "edit" ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ReceivedAmtModal;
